// Cliente de Analytics para enviar dados para a plataforma admin
// Este arquivo deve ser usado nas aplicações clientes e prestadores

interface AnalyticsEvent {
  event_type: string
  event_name: string
  user_id?: string
  user_type?: 'cliente' | 'prestador'
  session_id?: string
  page_url?: string
  referrer?: string
  user_agent?: string
  ip_address?: string
  metadata?: Record<string, unknown>
}

interface AnalyticsPageView {
  page_url: string
  page_title?: string
  user_id?: string
  user_type?: 'cliente' | 'prestador'
  session_id?: string
  referrer?: string
  user_agent?: string
  ip_address?: string
  time_on_page?: number
}

interface AnalyticsError {
  error_type: string
  error_message: string
  error_stack?: string
  user_id?: string
  user_type?: 'cliente' | 'prestador'
  session_id?: string
  page_url?: string
  user_agent?: string
  ip_address?: string
  metadata?: Record<string, unknown>
}

interface AnalyticsPerformance {
  metric_name: string
  metric_value: number
  metric_unit?: string
  user_id?: string
  user_type?: 'cliente' | 'prestador'
  session_id?: string
  page_url?: string
  user_agent?: string
  ip_address?: string
  metadata?: Record<string, unknown>
}

class AnalyticsClient {
  private adminUrl: string
  private appSource: 'clientes' | 'prestadores'
  private sessionId: string
  private isEnabled: boolean = false

  constructor(adminUrl: string, appSource: 'clientes' | 'prestadores') {
    this.adminUrl = adminUrl
    this.appSource = appSource
    this.sessionId = this.generateSessionId()
    // Habilitar analytics somente se em produção e com URL válida (https)
    const isHttpsUrl = /^https:\/\//.test(adminUrl)
    this.isEnabled = import.meta.env.PROD && isHttpsUrl
  }

  private generateSessionId(): string {
    return 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now()
  }

  private getCommonData() {
    return {
      session_id: this.sessionId,
      user_agent: navigator.userAgent,
      page_url: window.location.href,
      referrer: document.referrer
    }
  }

  private async sendData(type: string, data: Record<string, unknown>) {
    if (!this.isEnabled) {
      console.log(`📊 Analytics (${type}):`, data)
      return
    }

    try {
      if (!this.isEnabled) return
      if (!this.adminUrl) return
      if (!/^https:\/\//.test(this.adminUrl)) return
      const response = await fetch(`${this.adminUrl}/api/analytics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type,
          data: {
            ...data,
            ...this.getCommonData()
          },
          appSource: this.appSource
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('❌ Erro ao enviar dados de analytics:', error)
    }
  }

  async trackEvent(event: Omit<AnalyticsEvent, 'session_id' | 'user_agent' | 'page_url' | 'referrer'>) {
    return this.sendData('event', event)
  }

  async trackPageView(pageView: Omit<AnalyticsPageView, 'session_id' | 'user_agent' | 'page_url' | 'referrer'>) {
    return this.sendData('pageview', pageView)
  }

  async trackError(error: Omit<AnalyticsError, 'session_id' | 'user_agent' | 'page_url' | 'referrer'>) {
    return this.sendData('error', error)
  }

  async trackPerformance(performance: Omit<AnalyticsPerformance, 'session_id' | 'user_agent' | 'page_url' | 'referrer'>) {
    return this.sendData('performance', performance)
  }

  async updateUser(userId: string, userType: 'cliente' | 'prestador') {
    return this.sendData('user', { user_id: userId, user_type: userType })
  }

  // Métodos de conveniência
  async trackLogin(userId: string, userType: 'cliente' | 'prestador') {
    await this.trackEvent({
      event_type: 'authentication',
      event_name: 'user_login',
      user_id: userId,
      user_type: userType
    })
    await this.updateUser(userId, userType)
  }

  async trackLogout(userId: string, userType: 'cliente' | 'prestador') {
    await this.trackEvent({
      event_type: 'authentication',
      event_name: 'user_logout',
      user_id: userId,
      user_type: userType
    })
  }

  async trackServiceView(serviceId: string, userId?: string, userType?: 'cliente' | 'prestador') {
    await this.trackEvent({
      event_type: 'service',
      event_name: 'service_view',
      user_id: userId,
      user_type: userType,
      metadata: { service_id: serviceId }
    })
  }

  async trackBooking(bookingId: string, userId: string, userType: 'cliente' | 'prestador') {
    await this.trackEvent({
      event_type: 'booking',
      event_name: 'booking_created',
      user_id: userId,
      user_type: userType,
      metadata: { booking_id: bookingId }
    })
  }

  async trackPayment(paymentId: string, amount: number, userId: string, userType: 'cliente' | 'prestador') {
    await this.trackEvent({
      event_type: 'payment',
      event_name: 'payment_completed',
      user_id: userId,
      user_type: userType,
      metadata: { 
        payment_id: paymentId,
        amount: amount
      }
    })
  }

  // Monitoramento de erros automático
  setupErrorMonitoring() {
    if (!this.isEnabled) return

    window.addEventListener('error', (event) => {
      this.trackError({
        error_type: 'javascript_error',
        error_message: event.message,
        error_stack: event.error?.stack
      })
    })

    window.addEventListener('unhandledrejection', (event) => {
      this.trackError({
        error_type: 'unhandled_promise_rejection',
        error_message: event.reason?.message || 'Unknown error',
        error_stack: event.reason?.stack
      })
    })
  }

  // Monitoramento de performance
  setupPerformanceMonitoring() {
    if (!this.isEnabled) return

    // Monitorar tempo de carregamento da página
    window.addEventListener('load', () => {
      const loadTime = performance.now()
      this.trackPerformance({
        metric_name: 'page_load_time',
        metric_value: loadTime,
        metric_unit: 'ms'
      })
    })

    // Monitorar métricas de performance da web
    if ('performance' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'navigation') {
            const navEntry = entry as PerformanceNavigationTiming
            this.trackPerformance({
              metric_name: 'dom_content_loaded',
              metric_value: navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart,
              metric_unit: 'ms'
            })
          }
        }
      })
      observer.observe({ entryTypes: ['navigation'] })
    }
  }

  // Habilitar/desabilitar analytics
  enable() {
    this.isEnabled = true
  }

  disable() {
    this.isEnabled = false
  }
}

// Exportar instância para app clientes
export const analyticsClient = new AnalyticsClient(
  // Não usar localhost em produção
  (import.meta.env.PROD ? (import.meta.env.VITE_ADMIN_URL || '') : (import.meta.env.VITE_ADMIN_URL || 'http://localhost:3001')),
  'clientes'
)

// Configurar monitoramento automático
if (typeof window !== 'undefined') {
  analyticsClient.setupErrorMonitoring()
  analyticsClient.setupPerformanceMonitoring()
} 